
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Mail, Lock, Loader, MailCheck, Play } from 'lucide-react';
import { Separator } from '@/components/ui/separator';

interface LoginFormProps {
  onSubmit: (email: string, password: string) => Promise<void>;
  onResetPassword: (email: string) => Promise<void>;
  onResendConfirmation: (email: string) => Promise<void>;
  isLoading: boolean;
}

const DEMO_EMAIL = 'demo@campchefpro.com';
const DEMO_PASSWORD = 'TryDemo2025!';

export const LoginForm = ({ onSubmit, onResetPassword, onResendConfirmation, isLoading }: LoginFormProps) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await onSubmit(email, password);
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    await onResetPassword(email);
  };

  const handleDemoLogin = () => {
    setEmail(DEMO_EMAIL);
    setPassword(DEMO_PASSWORD);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="email">Email</Label>
        <div className="relative">
          <Mail className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
          <Input
            id="email"
            type="email"
            placeholder="Enter your email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="pl-10"
            required
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="password">Password</Label>
        <div className="relative">
          <Lock className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
          <Input
            id="password"
            type="password"
            placeholder="Enter your password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="pl-10"
            required
          />
        </div>
      </div>

      <Button
        type="submit"
        className="w-full"
        disabled={isLoading}
      >
        {isLoading ? (
          <>
            <Loader className="mr-2 h-4 w-4 animate-spin" />
            Signing in...
          </>
        ) : (
          'Sign In'
        )}
      </Button>

      <Button
        type="button"
        variant="ghost"
        className="w-full"
        onClick={handleResetPassword}
        disabled={isLoading}
      >
        Forgot your password?
      </Button>

      <Button
        type="button"
        variant="link"
        className="w-full"
        onClick={() => onResendConfirmation(email)}
        disabled={isLoading || !email}
      >
        <MailCheck className="mr-2 h-4 w-4" />
        Resend confirmation email
      </Button>

      <div className="relative my-4">
        <Separator />
        <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-background px-2 text-xs text-muted-foreground uppercase">
          Or
        </span>
      </div>

      <Button
        type="button"
        variant="outline"
        className="w-full"
        onClick={handleDemoLogin}
        disabled={isLoading}
      >
        <Play className="mr-2 h-4 w-4" />
        Try Demo Account
      </Button>
      <p className="text-xs text-center text-muted-foreground mt-2">
        Explore Camp Chef Pro with full access (demo mode)
      </p>
    </form>
  );
};
